import qrcode from 'qrcode-generator';

// UTF-8, så att å, ä och ö i t.ex.
// Swish-meddelanden blir rätt.
qrcode.stringToBytes = (s: string) => Array.from(new TextEncoder().encode(s));

export interface QrLayout {
    /** Arkets sida i moduler, inklusive den vita kanten. */
    size: number;
    /** SVG-path med alla mörka moduler. */
    path: string;
    /** Det runda hålet för bönan, i moduler, eller null. */
    hole: { cx: number; cy: number; r: number } | null;
}

/** Hålet är en cirkel på ~27 % av kodens bredd — gott om marginal för felkorrigering H (30 %). */
const HOLE_SHARE = 0.27;
/** Luft mellan hålets kant och närmaste modul, i moduler. */
const HOLE_GAP = 0.4;

/**
 * Räknar fram QR-kodens moduler. Med `withHole` görs ett runt hål i mitten
 * som följer rutnätet: moduler som skulle skäras av tas bort helt i stället
 * för att täckas av en rund bricka, så kanten blir jämn runt bönan.
 */
export const qrLayout = (data: string, opts: { withHole?: boolean; quiet?: number } = {}): QrLayout => {
    const quiet = opts.quiet ?? 2;
    const qr = qrcode(0, opts.withHole ? 'H' : 'M');
    qr.addData(data);
    qr.make();
    const n = qr.getModuleCount();

    let hole: QrLayout['hole'] = null;
    if (opts.withHole) {
        let d = Math.round(n * HOLE_SHARE);
        if (d % 2 === 0) d += 1; // udda antal moduler, så att hålet ligger mitt i en modul
        hole = { cx: n / 2, cy: n / 2, r: d / 2 };
    }

    const inHole = (x: number, y: number) => {
        if (!hole) return false;
        // Närmaste punkt i modulens ruta till hålets mitt.
        const nx = Math.max(x, Math.min(hole.cx, x + 1));
        const ny = Math.max(y, Math.min(hole.cy, y + 1));
        return Math.hypot(nx - hole.cx, ny - hole.cy) < hole.r + HOLE_GAP;
    };

    let path = '';
    for (let y = 0; y < n; y++) {
        for (let x = 0; x < n; x++) {
            if (qr.isDark(y, x) && !inHole(x, y)) path += `M${x + quiet} ${y + quiet}h1v1h-1z`;
        }
    }

    return {
        size: n + quiet * 2,
        path,
        hole: hole ? { cx: hole.cx + quiet, cy: hole.cy + quiet, r: hole.r } : null,
    };
};

/**
 * QR-koden som en enda SVG-path, en rektangel per följd av mörka moduler i
 * varje rad. Allt i en fyllning, så att inga hårfina skarvar syns mellan
 * modulerna. Används för vektor-QR i PDF (QR-koder att klistra på kataloger).
 */
export const qrRunsPath = (data: string, opts: { quiet?: number; level?: 'L' | 'M' | 'Q' | 'H' } = {}): { size: number; path: string } => {
    const quiet = opts.quiet ?? 4;
    const qr = qrcode(0, opts.level ?? 'M');
    qr.addData(data);
    qr.make();
    const n = qr.getModuleCount();
    let path = '';
    for (let y = 0; y < n; y++) {
        let x = 0;
        while (x < n) {
            if (!qr.isDark(y, x)) { x++; continue; }
            const start = x;
            while (x < n && qr.isDark(y, x)) x++;
            path += `M${start + quiet} ${y + quiet}h${x - start}v1h${start - x}z`;
        }
    }
    return { size: n + quiet * 2, path };
};
